import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { FaFileExcel } from 'react-icons/fa';
import { showCurrency } from '../../utils';
import { Section, StatStrip, DataTable, IdChip, Pill, Button, ExportModal, buildXlsxExport, exportAmount } from '../../components/EntityList';
import { useLabels } from '../../hooks/useLabels';
import { useClientSort } from '../../hooks/useClientSort';
import { dateValue, parseDueNumber } from '../../utils/tableSort';

function formatDate(date) {
	return date ? moment.utc(date).format('DD/MM/YYYY') : '';
}

function formatMoney(currency, amount) {
	if (amount === null || amount === undefined) {
		return '';
	}
	return `${showCurrency(currency) || ''} ${Number(amount).toLocaleString('es-UY')}`.trim();
}

function money(due, value) {
	return <span className='tw:font-mono tw:tabular-nums'>{formatMoney(due.credit?.currency, value)}</span>;
}

function CreditDues({
	rows = [],
	loading,
	error,
	expiredCredit,
	allDuesGenerated,
	nextExpirationDate,
	dueNumber,
	generationDate,
}) {
	const { t } = useLabels();
	const [exportOpen, setExportOpen] = useState(false);

	const { sortedRows, sortColumn, direction, onSort } = useClientSort(rows, {
		defaultColumn: 'nro',
		accessors: {
			nro: (due) => parseDueNumber(due.customId),
			customId: (due) => due.customId,
			date: (due) => dateValue(due.date),
			expiration: (due) => dateValue(due.expiration),
		},
	});

	const stats = useMemo(() => {
		const pending = rows.filter((due) => !due.paid);
		const outstandingByCurrency = new Map();
		pending.forEach((due) => {
			const key = due.credit?.currency || '—';
			outstandingByCurrency.set(key, (outstandingByCurrency.get(key) || 0) + (due.unpaidAmount || 0));
		});
		return [
			{ label: t('entity.due.plural'), value: rows.length },
			{ label: 'Pendientes', value: pending.length },
			{
				label: t('field.unpaidAmount'),
				value:
					[...outstandingByCurrency.entries()]
						.map(([currency, amount]) => formatMoney(currency, amount))
						.join(' · ') || '—',
				mono: true,
			},
		];
	}, [rows, t]);

	const columns = [
		{
			key: 'nro',
			header: 'Nro',
			sortable: true,
			className: 'tw:font-mono tw:tabular-nums',
			render: (due) => parseDueNumber(due.customId) ?? <span className='tw:text-ink-3'>—</span>,
		},
		{ key: 'customId', header: t('entity.due'), sortable: true, render: (due) => <IdChip>{due.customId}</IdChip> },
		{ key: 'date', header: t('field.date'), sortable: true, render: (due) => formatDate(due.date) },
		{ key: 'expiration', header: t('field.expiration'), sortable: true, render: (due) => formatDate(due.expiration) },
		{
			key: 'interest',
			header: t('field.interest'),
			className: 'tw:text-right',
			render: (due) => money(due, due.interest),
		},
		{
			key: 'amortization',
			header: t('field.amortization'),
			className: 'tw:text-right',
			render: (due) => money(due, due.amortization),
		},
		{
			key: 'amount',
			header: t('field.amount'),
			className: 'tw:text-right',
			render: (due) => money(due, due.amount),
		},
		{
			key: 'unpaidAmount',
			header: t('field.unpaidAmount'),
			className: 'tw:text-right',
			render: (due) => money(due, due.unpaidAmount),
		},
		{
			key: 'paid',
			header: t('field.paid'),
			render: (due) => <Pill tone={due.paid ? 'ok' : 'neutral'}>{due.paid ? 'Pagada' : 'Pendiente'}</Pill>,
		},
	];

	const exportFields = [
		{ key: 'nro', label: 'Nro' },
		{ key: 'customId', label: t('entity.due'), locked: true },
		{ key: 'date', label: t('field.date') },
		{ key: 'expiration', label: t('field.expiration') },
		{ key: 'currency', label: t('field.currency') },
		{ key: 'interest', label: t('field.interest') },
		{ key: 'amortization', label: t('field.amortization') },
		{ key: 'amount', label: t('field.amount') },
		{ key: 'unpaidAmount', label: t('field.unpaidAmount') },
		{ key: 'paid', label: t('field.paid') },
	];

	const exportGetters = {
		nro: (due) => parseDueNumber(due.customId) ?? '',
		customId: (due) => due.customId || '',
		date: (due) => formatDate(due.date),
		expiration: (due) => formatDate(due.expiration),
		currency: (due) => due.credit?.currency || '',
		interest: (due) => exportAmount(due.interest),
		amortization: (due) => exportAmount(due.amortization),
		amount: (due) => exportAmount(due.amount),
		unpaidAmount: (due) => exportAmount(due.unpaidAmount),
		paid: (due) => (due.paid ? 'Pagada' : 'Pendiente'),
	};

	const handleExport = (selectedKeys) =>
		buildXlsxExport({
			rows: sortedRows,
			fields: exportFields,
			selectedKeys,
			getters: exportGetters,
			sheetName: 'Cuotas',
			filePrefix: 'cuotas',
		});

	const note = expiredCredit
		? t('msg.creditExpired')
		: allDuesGenerated
			? t('msg.allDuesGenerated')
			: t('msg.nextExpirationTemplate')
				.replace('{date}', nextExpirationDate)
				.replace('{number}', dueNumber)
				.replace('{generationDate}', generationDate);

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			<StatStrip stats={stats} />
			<div className='tw:flex tw:items-center tw:justify-between tw:gap-3'>
				<span className='tw:text-[12.5px] tw:text-ink-2'>{note}</span>
				<Button size='sm' icon={<FaFileExcel />} onClick={() => setExportOpen(true)} disabled={rows.length === 0}>
					{t('action.export')}
				</Button>
			</div>
			<Section title={t('entity.due.plural')}>
				<DataTable
					columns={columns}
					rows={sortedRows}
					loading={loading}
					error={error}
					sortColumn={sortColumn}
					direction={direction}
					onSort={onSort}
					emptyMessage='No se encontraron cuotas para este crédito.'
				/>
			</Section>

			<ExportModal
				show={exportOpen}
				onClose={() => setExportOpen(false)}
				count={rows.length}
				fields={exportFields}
				subtitle={t('entity.due.plural')}
				onExport={handleExport}
			/>
		</div>
	);
}

export default CreditDues;
